/**
 * The export surface (Step 3 quick actions, Step 4 export center).
 *
 * Block 4 wires the surface and the gate; the generators themselves
 * are Block 5 (brief Section 12). They register here, behind this
 * interface, so Block 5 drops in without UI or store rework:
 *
 *   registry.register('results-csv', generateResultsCsv)  // Block 5
 *
 * Gating (S3): `requestExport` on the store refuses while the
 * license is inactive — the refusal is a state-layer outcome, not a
 * hidden button. In trial the UI explains the lock (the mockup's lock
 * note). When licensed, a kind with no generator yet reports
 * 'unavailable'; that wording is development-only and never ships as
 * product copy — Block 5 fills every kind before launch.
 */

import type { ProjectState } from '../domain/types.js';

export type ExportKind =
  | 'results-csv'
  | 'area-report-xlsx'
  | 'master-dataset'
  | 'methods-report-docx'
  | 'reproducibility-zip';

export interface ExportKindInfo {
  readonly kind: ExportKind;
  readonly label: string;
  readonly detail: string;
}

/**
 * The export center list (mockup Step 4, plus the brief 12.3 master
 * dataset). Order follows the owner's EXPORT STANDARD primacy rule
 * (2026-10-30): the formatted report exports lead — Excel first,
 * then the Word methods report (SAE has no PDF export; the DOCX is
 * its formatted narrative report) and the master dataset with its
 * Excel workbook — and the plain-data CSV comes last, presented as
 * the secondary plain-data option (CSV carries no formatting).
 */
export const EXPORT_KINDS: readonly ExportKindInfo[] = [
  {
    kind: 'area-report-xlsx',
    label: 'Per-Area Report (.xlsx)',
    detail: 'One formatted workbook per area, in the regional-table layout.',
  },
  {
    kind: 'methods-report-docx',
    label: 'Methods Report (.docx)',
    detail: 'Model, transform, MSE method, and every rule that fired.',
  },
  {
    kind: 'master-dataset',
    label: 'Master Results Dataset',
    detail: 'Every domain and reliability measure, in .xlsx, .csv, and .sav.',
  },
  {
    kind: 'reproducibility-zip',
    label: 'Reproducibility Package (.zip)',
    detail: 'Inputs, configuration, seed, manifest, and the R reproduction script.',
  },
  {
    kind: 'results-csv',
    label: 'Results Table (.csv)',
    detail:
      'The estimates table as shown, with flags and intervals — plain data; CSV carries no formatting.',
  },
];

export interface ExportArtifact {
  readonly filename: string;
  readonly mimeType: string;
  readonly data: Blob | Uint8Array | string;
}

export interface ExportContext {
  readonly state: ProjectState;
}

/** A Block 5 generator: project state in, one downloadable file out. */
export type ExportGenerator = (
  context: ExportContext,
) => Promise<ExportArtifact>;

export type ExportOutcome =
  | {
      readonly status: 'locked';
      readonly kind: ExportKind;
      /** User-facing: why exports are locked and how to unlock. */
      readonly message: string;
    }
  | {
      readonly status: 'unavailable';
      readonly kind: ExportKind;
      /** Development-only detail; the UI shows it only in dev builds. */
      readonly message: string;
    }
  | {
      readonly status: 'ready';
      readonly kind: ExportKind;
      readonly artifact: ExportArtifact;
    }
  | {
      readonly status: 'no-results';
      readonly kind: ExportKind;
      readonly message: string;
    };

export const EXPORT_LOCKED_MESSAGE =
  'Exports require an active license. Enter the email address used at purchase and the license key from your receipt email to unlock every export for this project.';

export class ExportUnavailableError extends Error {
  constructor(kind: ExportKind) {
    super(`No generator is registered for export kind "${kind}" in this build.`);
    this.name = 'ExportUnavailableError';
  }
}

export class ExportRegistry {
  readonly #generators = new Map<ExportKind, ExportGenerator>();

  register(kind: ExportKind, generator: ExportGenerator): void {
    this.#generators.set(kind, generator);
  }

  has(kind: ExportKind): boolean {
    return this.#generators.has(kind);
  }

  async generate(
    kind: ExportKind,
    context: ExportContext,
  ): Promise<ExportArtifact> {
    const generator = this.#generators.get(kind);
    if (generator === undefined) throw new ExportUnavailableError(kind);
    return generator(context);
  }
}
